/**
 * Utility functions for managing user preferences
 * Stores user preferences like default view page
 */

const USER_PREFERENCES_KEY = 'userPreferences';

/**
 * Get user preferences from localStorage
 * @param {string} username - The user's username/email to scope preferences
 * @returns {Object} - The user preferences object
 */
export const getUserPreferences = (username) => {
    if (!username) return getDefaultPreferences();
    
    try {
        const allPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
        if (!allPreferences) return getDefaultPreferences();
        
        const preferences = JSON.parse(allPreferences);
        return preferences[username] || getDefaultPreferences();
    } catch (error) {
        console.error('Error reading user preferences:', error);
        return getDefaultPreferences();
    }
};

/**
 * Save user preferences to localStorage
 * @param {string} username - The user's username/email to scope preferences
 * @param {Object} preferences - The preferences object to save
 */
export const saveUserPreferences = (username, preferences) => {
    if (!username) {
        console.warn('Cannot save preferences without username');
        return;
    }
    
    try {
        const allPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
        const preferencesObj = allPreferences ? JSON.parse(allPreferences) : {};
        
        preferencesObj[username] = {
            ...preferences,
            lastUpdated: Date.now()
        };
        
        localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferencesObj));
    } catch (error) {
        console.error('Error saving user preferences:', error);
    }
};

/**
 * Get default preferences
 * @returns {Object} - Default preferences object
 */
export const getDefaultPreferences = () => ({
    emailNotifications: true,
    pushNotifications: false,
    defaultView: 'home'
});

/**
 * Clear preferences for a specific user
 * @param {string} username - The user's username/email
 */
export const clearUserPreferences = (username) => {
    if (!username) return;
    
    try {
        const allPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
        if (!allPreferences) return;
        
        const preferencesObj = JSON.parse(allPreferences);
        delete preferencesObj[username];
        
        localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferencesObj));
    } catch (error) {
        console.error('Error clearing user preferences:', error);
    }
};

/**
 * Clear all user preferences
 */
export const clearAllPreferences = () => {
    localStorage.removeItem(USER_PREFERENCES_KEY);
};
